import { FormLayout, FormField } from '../../types/form';

// ============================================================================
// Field Order Management
// ============================================================================
export const updateFieldOrder = ({
  layout,
  setLayout,
  from,
  to,
  activeId,
  overId
}: {
  layout: FormLayout['layout_config'];
  setLayout: React.Dispatch<React.SetStateAction<FormLayout['layout_config']>>;
  from: string;
  to: string;
  activeId: string;
  overId: string;
}) => {
  if (from !== to) return;

  const fieldsInSection = layout.fields.filter(f => f.sectionId === from).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const fromIndex = fieldsInSection.findIndex(f => f.id === activeId);
  const toIndex = fieldsInSection.findIndex(f => f.id === overId);

  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

  const reordered = [...fieldsInSection];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);

  reordered.forEach((f, i) => (f.order = i));

  const others = layout.fields.filter(f => f.sectionId !== from);
  setLayout({ ...layout, fields: [...others, ...reordered] });
};

// ============================================================================
// Field Section Management
// ============================================================================
export const moveFieldToSection = ({
  layout,
  setLayout,
  sectionId,
  field
}: {
  layout: FormLayout['layout_config'];
  setLayout: React.Dispatch<React.SetStateAction<FormLayout['layout_config']>>;
  sectionId: string;
  field: FormField;
}) => {
  const nextOrder = layout.fields.filter(f => f.sectionId === sectionId).length;

  setLayout(prev => ({
    ...prev,
    fields: [
      ...prev.fields,
      {
        id: `field-${field.field_name}`,
        name: field.field_name,
        label: field.label || field.field_name,
        type: field.type || 'text',
        width: 100,
        required: !!field.required,
        sectionId,
        sectionTitle: prev.sections.find(s => s.id === sectionId)?.title || '',
        order: nextOrder
      }
    ]
  }));
};

// ============================================================================
// Field Removal
// ============================================================================
export const removeFieldFromLayout = ({
  layout,
  setLayout,
  availableFields,
  setAvailableFields,
  activeId
}: {
  layout: FormLayout['layout_config'];
  setLayout: React.Dispatch<React.SetStateAction<FormLayout['layout_config']>>;
  availableFields: FormField[];
  setAvailableFields: React.Dispatch<React.SetStateAction<FormField[]>>;
  activeId: string;
}) => {
  const field = layout.fields.find(f => f.id === activeId);
  if (!field) return;

  const newAvailable = {
    field_name: field.name,
    label: field.label,
    type: field.type,
    required: field.required
  };

  setAvailableFields([...availableFields, newAvailable as FormField]);
  setLayout({
    ...layout,
    fields: layout.fields.filter(f => f.id !== activeId)
  });
};
